import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import {
  Popover,
  RobotoButton,
  type RobotoButtonVariant,
} from "@/shared/foundation/primitives";

import { CreateDevice } from "./CreateDevice";

interface CreateDeviceButtonProps {
  /**
   * Trigger style: the solid CTA in the page toolbar, or the neutral outlined
   * one the empty state shows. Passed straight through to {@link RobotoButton}.
   * @default "primary"
   */
  variant?: RobotoButtonVariant;
  /**
   * Where the create-device form opens relative to the button. The toolbar
   * sits at the right edge, so the form hangs off the button's end; the empty
   * state is centered, so the form centers under it.
   * @default "end"
   */
  align?: "start" | "center" | "end";
  /** Device type the form starts on. */
  deviceTypeId?: string;
}

/**
 * "New Device" action for the devices page and a device type's Devices tab.
 * Opens the create-device form in a popover anchored to the button, with
 * `deviceTypeId` preselected as the type when given; the form closes the
 * popover once the device is created.
 */
export function CreateDeviceButton({
  variant = "primary",
  align = "end",
  deviceTypeId,
}: CreateDeviceButtonProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <RobotoButton
            variant={variant}
            icon={<PlusIcon aria-hidden="true" />}
          >
            New Device
          </RobotoButton>
        }
      />
      <Popover.Content align={align} aria-label="Create device">
        <CreateDevice
          close={() => setOpen(false)}
          deviceTypeId={deviceTypeId}
        />
      </Popover.Content>
    </Popover>
  );
}
